import { Box, Flex, IconButton } from '@chakra-ui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'

interface HorizontalScrollContainerProps {
  children: React.ReactNode
  gap?: string
  mb?: string
}

/**
 * Horizontal scroll container with a custom draggable scrollbar at the TOP.
 * The native scrollbar is hidden; interaction happens via the track + thumb
 * rendered above the cards, plus left/right arrow buttons.
 */
export function HorizontalScrollContainer({
  children,
  gap = '1.5rem',
  mb = '2.31rem',
}: HorizontalScrollContainerProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const thumbRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const dragDataRef = useRef({ mouseX: 0, scrollLeft: 0 })

  const [thumbStyle, setThumbStyle] = useState({ widthPct: 100, leftPct: 0 })

  const updateThumb = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    const scrollable = scrollWidth - clientWidth
    const widthPct = Math.max((clientWidth / scrollWidth) * 100, 10)
    const leftPct = scrollable > 0 ? (scrollLeft / scrollable) * (100 - widthPct) : 0
    setThumbStyle({ widthPct, leftPct })
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateThumb()
    el.addEventListener('scroll', updateThumb, { passive: true })
    const ro = new ResizeObserver(updateThumb)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateThumb)
      ro.disconnect()
    }
  }, [updateThumb])

  // Drag: attach to window so mouse leaving the thumb doesn't break the drag
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return
      const el = scrollRef.current
      const track = trackRef.current
      if (!el || !track) return
      const dx = e.clientX - dragDataRef.current.mouseX
      const trackW = track.clientWidth
      const scrollable = el.scrollWidth - el.clientWidth
      const thumbW = (el.clientWidth / el.scrollWidth) * trackW
      const draggableW = trackW - thumbW
      if (draggableW <= 0) return
      el.scrollLeft = Math.max(
        0,
        Math.min(dragDataRef.current.scrollLeft + (dx * scrollable) / draggableW, scrollable)
      )
    }
    const onMouseUp = () => {
      if (!isDraggingRef.current) return
      isDraggingRef.current = false
      document.body.style.userSelect = ''
      if (thumbRef.current) thumbRef.current.style.cursor = 'grab'
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    const el = scrollRef.current
    if (!el) return
    isDraggingRef.current = true
    dragDataRef.current = { mouseX: e.clientX, scrollLeft: el.scrollLeft }
    document.body.style.userSelect = 'none'
    if (thumbRef.current) thumbRef.current.style.cursor = 'grabbing'
  }

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (thumbRef.current && e.target === thumbRef.current) return
    const el = scrollRef.current
    const track = trackRef.current
    if (!el || !track) return
    const rect = track.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const trackW = rect.width
    const thumbW = (thumbStyle.widthPct / 100) * trackW
    const clampedLeft = Math.max(0, Math.min(clickX - thumbW / 2, trackW - thumbW))
    const draggableW = trackW - thumbW
    el.scrollLeft = draggableW > 0 ? (clampedLeft / draggableW) * (el.scrollWidth - el.clientWidth) : 0
  }

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current
    if (!el) return
    const amount = Math.max(el.clientWidth * 0.65, 360)
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' })
  }

  const showScrollbar = thumbStyle.widthPct < 99

  return (
    <Box mb={mb}>
      {showScrollbar && (
        <Flex align="center" gap="0.375rem" mb="0.5rem">
          <IconButton
            icon={<FiChevronLeft size={14} />}
            aria-label="Scroll left"
            size="xs"
            variant="ghost"
            isDisabled={thumbStyle.leftPct <= 0.5}
            onClick={() => scroll('left')}
            color="gray.500"
            _hover={{ color: 'gray.800', bg: 'gray.100' }}
            _disabled={{ opacity: 0.3, cursor: 'default' }}
            borderRadius="md"
            minW="1.25rem"
            h="1.25rem"
          />

          {/* Track */}
          <Box
            ref={trackRef}
            flex={1}
            h="10px"
            bg="gray.100"
            borderRadius="5px"
            position="relative"
            cursor="pointer"
            onClick={handleTrackClick}
            _hover={{ bg: 'gray.200' }}
            transition="background 0.15s"
          >
            {/* Thumb */}
            <Box
              ref={thumbRef}
              position="absolute"
              top="1px"
              h="8px"
              bg="gray.400"
              borderRadius="4px"
              cursor="grab"
              _hover={{ bg: 'gray.500' }}
              transition="left 0.05s, background 0.15s"
              style={{
                width: `${thumbStyle.widthPct}%`,
                left: `${thumbStyle.leftPct}%`,
              }}
              onMouseDown={handleThumbMouseDown}
            />
          </Box>

          <IconButton
            icon={<FiChevronRight size={14} />}
            aria-label="Scroll right"
            size="xs"
            variant="ghost"
            isDisabled={thumbStyle.leftPct + thumbStyle.widthPct >= 99.5}
            onClick={() => scroll('right')}
            color="gray.500"
            _hover={{ color: 'gray.800', bg: 'gray.100' }}
            _disabled={{ opacity: 0.3, cursor: 'default' }}
            borderRadius="md"
            minW="1.25rem"
            h="1.25rem"
          />
        </Flex>
      )}

      {/* Content — native scrollbar hidden, interaction via custom track above */}
      <Box
        ref={scrollRef}
        overflowX="auto"
        sx={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Flex gap={gap} flexWrap="nowrap" alignItems="flex-start">
          {children}
        </Flex>
      </Box>
    </Box>
  )
}
